import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Star, Play, X, ChevronLeft, ChevronRight, Film, Tv, User, Loader, ExternalLink, Calendar, Clock, Heart, Bookmark } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TABS = [
  { id: 'trending', label: 'Tendencias', icon: '🔥' },
  { id: 'movies', label: 'Películas', icon: '🎬' },
  { id: 'series', label: 'Series', icon: '📺' },
  { id: 'search', label: 'Buscar', icon: '🔍' },
];

const GENRES_MOVIE = [
  { id: 28, label: 'Acción' }, { id: 12, label: 'Aventura' }, { id: 16, label: 'Animación' },
  { id: 35, label: 'Comedia' }, { id: 80, label: 'Crimen' }, { id: 18, label: 'Drama' },
  { id: 14, label: 'Fantasía' }, { id: 27, label: 'Terror' }, { id: 10749, label: 'Romance' },
  { id: 878, label: 'Ciencia Ficción' }, { id: 53, label: 'Thriller' },
];

const SORT_OPTIONS = [
  { value: 'popularity.desc', label: 'Más populares' },
  { value: 'vote_average.desc', label: 'Mejor valoradas' },
  { value: 'primary_release_date.desc', label: 'Más recientes' },
  { value: 'revenue.desc', label: 'Mayor taquilla' },
];

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-1">
      <Star size={12} className="text-yellow-400 fill-yellow-400" />
      <span className="text-yellow-400 text-xs font-bold">{rating.toFixed(1)}</span>
    </div>
  );
}

function MovieCard({ item, onClick }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div className="cursor-pointer group" onClick={() => onClick(item)}>
      <div className="relative aspect-[2/3] rounded-xl overflow-hidden bg-gray-800 shadow-lg group-hover:shadow-2xl transition-all duration-300 group-hover:scale-105">
        {item.poster_url && !imgError ? (
          <img src={item.poster_url} alt={item.title} className="w-full h-full object-cover" onError={() => setImgError(true)} loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-800">
            {item.media_type === 'tv' ? <Tv size={40} className="text-gray-600" /> : <Film size={40} className="text-gray-600" />}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <p className="text-white text-xs font-medium line-clamp-2">{item.title}</p>
          <div className="flex items-center justify-between mt-1">
            {item.rating > 0 && <StarRating rating={item.rating} />}
            {item.year && <span className="text-gray-400 text-xs">{item.year}</span>}
          </div>
        </div>
        <div className="absolute top-2 right-2">
          <div className={`text-xs px-1.5 py-0.5 rounded font-bold ${item.media_type === 'tv' ? 'bg-blue-600 text-white' : 'bg-red-600 text-white'}`}>
            {item.media_type === 'tv' ? 'SERIE' : 'FILM'}
          </div>
        </div>
      </div>
      <div className="mt-2 px-0.5">
        <p className="text-white text-xs font-medium line-clamp-1">{item.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {item.rating > 0 && <StarRating rating={item.rating} />}
          {item.year && <span className="text-gray-500 text-xs">{item.year}</span>}
        </div>
      </div>
    </div>
  );
}

function PersonCard({ person, onClick }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div className="cursor-pointer group text-center" onClick={() => onClick(person)}>
      <div className="w-full aspect-[2/3] rounded-xl overflow-hidden bg-gray-800 group-hover:scale-105 transition-transform duration-300 shadow-lg">
        {person.poster_url && !imgError ? (
          <img src={person.poster_url} alt={person.title} className="w-full h-full object-cover" onError={() => setImgError(true)} loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User size={40} className="text-gray-600" />
          </div>
        )}
      </div>
      <p className="text-white text-xs font-medium mt-1.5 line-clamp-1">{person.title || person.name}</p>
      {person.known_for_department && <p className="text-gray-500 text-xs">{person.known_for_department}</p>}
    </div>
  );
}

function DetailModal({ item, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrailer, setShowTrailer] = useState(null);
  const [personDetail, setPersonDetail] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (item.media_type === 'person') {
          const r = await axios.get(`${API}/movies/person/${item.id}`);
          setPersonDetail(r.data);
        } else {
          const mt = item.media_type === 'tv' ? 'tv' : 'movie';
          const r = await axios.get(`${API}/movies/detail/${mt}/${item.id}`);
          setDetail(r.data);
        }
      } catch (e) {
        toast.error('Error al cargar detalles');
        onClose();
      } finally { setLoading(false); }
    };
    load();
  }, [item.id, item.media_type, onClose]);

  const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  if (loading) return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={handleBackdropClick}>
      <Loader size={40} className="text-red-400 animate-spin" />
    </div>
  );

  if (personDetail) {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto" onClick={handleBackdropClick}>
        <div className="min-h-full flex items-start justify-center p-4 py-8">
          <div className="bg-[#0d1117] rounded-2xl max-w-3xl w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="relative">
              <div className="h-40 bg-gradient-to-r from-blue-900 to-purple-900" />
              <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-black/50 rounded-full hover:bg-black/70 text-white"><X size={18} /></button>
              <div className="absolute bottom-0 left-6 translate-y-1/2">
                {personDetail.photo_url ? (
                  <img src={personDetail.photo_url} alt={personDetail.name} className="w-24 h-24 rounded-full border-4 border-[#0d1117] object-cover shadow-xl" />
                ) : (
                  <div className="w-24 h-24 rounded-full border-4 border-[#0d1117] bg-gray-800 flex items-center justify-center"><User size={32} className="text-gray-600" /></div>
                )}
              </div>
            </div>
            <div className="pt-16 pb-8 px-6">
              <h2 className="text-2xl font-bold text-white">{personDetail.name}</h2>
              {personDetail.known_for_department && <p className="text-red-400 text-sm mt-0.5">{personDetail.known_for_department}</p>}
              {personDetail.birthday && <p className="text-gray-400 text-sm mt-1 flex items-center gap-1"><Calendar size={12} />{personDetail.birthday}{personDetail.place_of_birth ? ` · ${personDetail.place_of_birth}` : ''}</p>}
              {personDetail.biography && <p className="text-gray-300 text-sm mt-4 leading-relaxed line-clamp-5">{personDetail.biography}</p>}
              {personDetail.movie_credits?.cast?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-white font-semibold mb-3">Filmografía destacada</h3>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {personDetail.movie_credits.cast.map(m => (
                      <div key={m.id} className="text-center">
                        <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gray-800">
                          {m.poster_url ? <img src={m.poster_url} alt={m.title} className="w-full h-full object-cover" /> : <Film size={20} className="text-gray-600 m-auto mt-4" />}
                        </div>
                        <p className="text-gray-400 text-xs mt-0.5 line-clamp-1">{m.title || m.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto" onClick={handleBackdropClick}>
      <div className="min-h-full flex items-start justify-center p-4 py-8">
        <div className="bg-[#0d1117] rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
          {showTrailer && ReactDOM.createPortal(
            <div className="fixed inset-0 bg-black/95 flex items-center justify-center" style={{ zIndex: 999999 }} onClick={() => setShowTrailer(null)}>
              <div className="relative w-full max-w-3xl px-4" onClick={e => e.stopPropagation()}>
                <button onClick={() => setShowTrailer(null)} className="absolute -top-10 right-4 p-2 text-white hover:text-red-400"><X size={24} /></button>
                <div className="aspect-video rounded-xl overflow-hidden">
                  <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${showTrailer}?autoplay=1`} allowFullScreen title="Trailer" allow="autoplay" />
                </div>
              </div>
            </div>,
            document.body
          )}
          <div className="relative">
            {detail.backdrop_url ? (
              <div className="relative h-64 sm:h-80 overflow-hidden">
                <img src={detail.backdrop_url} alt={detail.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117] via-[#0d1117]/60 to-transparent" />
              </div>
            ) : <div className="h-24 bg-gradient-to-r from-red-900/30 to-gray-900" />}
            <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-black/60 rounded-full hover:bg-black/80 text-white z-10"><X size={18} /></button>
          </div>
          <div className="px-6 pb-8 -mt-20 relative">
            <div className="flex gap-5">
              <div className="hidden sm:block flex-shrink-0 w-28">
                {detail.poster_url ? <img src={detail.poster_url} alt={detail.title} className="w-full rounded-xl shadow-2xl border-2 border-gray-800" /> : null}
              </div>
              <div className="flex-1 pt-20 sm:pt-0 sm:mt-16">
                <h2 className="text-2xl sm:text-3xl font-bold text-white">{detail.title}</h2>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  {detail.rating > 0 && <StarRating rating={detail.rating} />}
                  {detail.year && <span className="text-gray-400 text-sm flex items-center gap-1"><Calendar size={12} />{detail.year}</span>}
                  {(detail.runtime || detail.episode_run_time?.[0]) && (
                    <span className="text-gray-400 text-sm flex items-center gap-1"><Clock size={12} />{detail.runtime || detail.episode_run_time?.[0]} min</span>
                  )}
                  {detail.genres?.slice(0, 3).map(g => (
                    <span key={g.id} className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full">{g.name}</span>
                  ))}
                </div>
                {detail.trailers?.length > 0 && (
                  <div className="flex gap-2 mt-4">
                    {detail.trailers.slice(0, 2).map(t => (
                      <button key={t.key} onClick={() => setShowTrailer(t.key)}
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        <Play size={14} fill="white" /> {t.type === 'Teaser' ? 'Teaser' : 'Trailer'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {detail.overview && <p className="text-gray-300 mt-5 leading-relaxed">{detail.overview}</p>}
            {detail.credits?.cast?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-white font-semibold mb-3 text-lg">Reparto</h3>
                <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                  {detail.credits.cast.map(actor => (
                    <div key={actor.id} className="flex-shrink-0 w-20 text-center">
                      <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-800 mx-auto">
                        {actor.photo_url ? <img src={actor.photo_url} alt={actor.name} className="w-full h-full object-cover" /> : <User size={28} className="text-gray-600 m-auto mt-6" />}
                      </div>
                      <p className="text-white text-xs font-medium mt-1 line-clamp-1">{actor.name}</p>
                      <p className="text-gray-500 text-xs line-clamp-1">{actor.character}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {detail.similar?.results?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-white font-semibold mb-3 text-lg">Similares</h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                  {detail.similar.results.slice(0, 6).map(m => (
                    <div key={m.id} className="text-center">
                      <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gray-800">
                        {m.poster_url ? <img src={m.poster_url} alt={m.title} className="w-full h-full object-cover" /> : null}
                      </div>
                      <p className="text-gray-400 text-xs mt-0.5 line-clamp-1">{m.title}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function GridSection({ title, items, onItemClick, loading }) {
  if (loading) return (
    <div className="space-y-3">
      <div className="h-6 w-40 bg-white/10 rounded animate-pulse" />
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
        {Array.from({length: 12}).map((_, i) => <div key={i} className="aspect-[2/3] rounded-xl bg-white/5 animate-pulse" />)}
      </div>
    </div>
  );
  if (!items?.length) return null;
  return (
    <div className="space-y-3">
      {title && <h3 className="text-white font-bold text-lg">{title}</h3>}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
        {items.map(item => item.media_type === 'person'
          ? <PersonCard key={item.id} person={item} onClick={onItemClick} />
          : <MovieCard key={item.id} item={item} onClick={onItemClick} />
        )}
      </div>
    </div>
  );
}

function TrendingCarousel({ items, onItemClick }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % Math.min(items.length, 8)), 5000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items?.length) return null;
  const current = items[idx];
  return (
    <div className="relative rounded-2xl overflow-hidden cursor-pointer" style={{ height: 280 }} onClick={() => onItemClick(current)}>
      {current.backdrop_url ? (
        <img src={current.backdrop_url} alt={current.title} className="w-full h-full object-cover transition-opacity duration-700" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-red-900/40 to-gray-900" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-end gap-4">
          {current.poster_url && <img src={current.poster_url} alt={current.title} className="w-20 rounded-lg shadow-xl hidden sm:block flex-shrink-0" />}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded font-bold ${current.media_type === 'tv' ? 'bg-blue-600' : 'bg-red-600'} text-white`}>
                {current.media_type === 'tv' ? 'SERIE' : 'PELÍCULA'}
              </span>
              {current.rating > 0 && <StarRating rating={current.rating} />}
              {current.year && <span className="text-gray-300 text-xs">{current.year}</span>}
            </div>
            <h2 className="text-white text-2xl font-bold drop-shadow-lg line-clamp-1">{current.title}</h2>
            {current.overview && <p className="text-gray-300 text-sm mt-1 line-clamp-2 hidden sm:block">{current.overview}</p>}
            <div className="flex items-center gap-2 mt-2">
              <button className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors" onClick={e => { e.stopPropagation(); onItemClick(current); }}>
                <Play size={14} fill="white" /> Ver detalles
              </button>
            </div>
          </div>
        </div>
        <div className="flex gap-1.5 mt-3">
          {items.slice(0, 8).map((_, i) => (
            <button key={i} onClick={e => { e.stopPropagation(); setIdx(i); }}
              className={`h-1 rounded-full transition-all ${i === idx ? 'bg-red-500 w-6' : 'bg-white/30 w-1.5'}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MoviesModule() {
  const [activeTab, setActiveTab] = useState('trending');
  const [trending, setTrending] = useState([]);
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState({ trending: false, movies: false, series: false, search: false });
  const [selectedItem, setSelectedItem] = useState(null);
  const [moviePage, setMoviePage] = useState(1);
  const [seriePage, setSeriePage] = useState(1);
  const [movieSort, setMovieSort] = useState('popularity.desc');
  const [serieSort, setSerieSort] = useState('popularity.desc');
  const [movieGenre, setMovieGenre] = useState(null);
  const [serieGenre, setSerieGenre] = useState(null);
  const [moviePages, setMoviePages] = useState(1);
  const [seriePages, setSeriePages] = useState(1);

  const setLoad = (key, val) => setLoading(l => ({ ...l, [key]: val }));

  useEffect(() => {
    loadTrending();
  }, []);

  useEffect(() => { if (activeTab === 'movies') loadMovies(); }, [activeTab, moviePage, movieSort, movieGenre]);
  useEffect(() => { if (activeTab === 'series') loadSeries(); }, [activeTab, seriePage, serieSort, serieGenre]);

  const loadTrending = async () => {
    setLoad('trending', true);
    try {
      const r = await axios.get(`${API}/movies/trending`);
      setTrending(r.data.results || []);
    } catch { toast.error('Error al cargar tendencias'); }
    finally { setLoad('trending', false); }
  };

  const loadMovies = async () => {
    setLoad('movies', true);
    try {
      const r = await axios.get(`${API}/movies/discover`, { params: { media_type: 'movie', sort_by: movieSort, page: moviePage, ...(movieGenre ? { genre_id: movieGenre } : {}) } });
      setMovies(r.data.results || []);
      setMoviePages(r.data.total_pages || 1);
    } catch { toast.error('Error'); }
    finally { setLoad('movies', false); }
  };

  const loadSeries = async () => {
    setLoad('series', true);
    try {
      const r = await axios.get(`${API}/movies/discover`, { params: { media_type: 'tv', sort_by: serieSort, page: seriePage, ...(serieGenre ? { genre_id: serieGenre } : {}) } });
      setSeries(r.data.results || []);
      setSeriePages(r.data.total_pages || 1);
    } catch { toast.error('Error'); }
    finally { setLoad('series', false); }
  };

  const handleSearch = useCallback(async (q) => {
    if (!q.trim() || q.trim().length < 2) { setSearchResults([]); return; }
    setLoad('search', true);
    try {
      const r = await axios.get(`${API}/movies/search`, { params: { q } });
      setSearchResults(r.data.results || []);
    } catch { toast.error('Error en búsqueda'); }
    finally { setLoad('search', false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (activeTab === 'search') handleSearch(searchQuery); }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, activeTab, handleSearch]);

  return (
      <>
      <SectionAgent section="movies" />
      <div className="flex flex-col h-full bg-[#0d1117] text-white overflow-hidden">
      {selectedItem && <DetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />}

      <div className="flex-shrink-0 px-4 sm:px-6 pt-4 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-600/20 flex items-center justify-center flex-shrink-0">
            <Film size={20} className="text-red-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Pelis & Series</h1>
            <p className="text-gray-400 text-xs">Powered by TMDB</p>
          </div>
        </div>

        <div className="flex gap-1 p-1 bg-white/5 rounded-xl overflow-x-auto no-scrollbar">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${activeTab === tab.id ? 'bg-red-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <span>{tab.icon}</span>{tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 pb-6 space-y-6">
        {activeTab === 'trending' && (
          <>
            {loading.trending ? <div className="h-64 rounded-2xl bg-white/5 animate-pulse" /> : <TrendingCarousel items={trending} onItemClick={setSelectedItem} />}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {loading.trending
                ? Array.from({length: 12}).map((_, i) => <div key={i} className="aspect-[2/3] rounded-xl bg-white/5 animate-pulse" />)
                : trending.slice(0, 18).map(item => item.media_type === 'person'
                    ? <PersonCard key={item.id} person={item} onClick={setSelectedItem} />
                    : <MovieCard key={item.id} item={item} onClick={setSelectedItem} />
                  )
              }
            </div>
          </>
        )}

        {activeTab === 'movies' && (
          <>
            <div className="flex flex-wrap gap-2">
              <select value={movieSort} onChange={e => { setMovieSort(e.target.value); setMoviePage(1); }}
                className="bg-white/5 text-gray-300 text-sm rounded-lg px-3 py-1.5 outline-none border border-white/10 hover:border-white/20">
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={movieGenre || ''} onChange={e => { setMovieGenre(e.target.value ? Number(e.target.value) : null); setMoviePage(1); }}
                className="bg-white/5 text-gray-300 text-sm rounded-lg px-3 py-1.5 outline-none border border-white/10 hover:border-white/20">
                <option value="">Todos los géneros</option>
                {GENRES_MOVIE.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
              </select>
            </div>
            <GridSection items={movies} onItemClick={setSelectedItem} loading={loading.movies} />
            <div className="flex items-center justify-center gap-3 pt-2">
              <button disabled={moviePage <= 1 || loading.movies} onClick={() => setMoviePage(p => p - 1)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 transition-colors"><ChevronLeft size={18} /></button>
              <span className="text-gray-400 text-sm">{moviePage} / {Math.min(moviePages, 500)}</span>
              <button disabled={moviePage >= moviePages || loading.movies} onClick={() => setMoviePage(p => p + 1)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 transition-colors"><ChevronRight size={18} /></button>
            </div>
          </>
        )}

        {activeTab === 'series' && (
          <>
            <div className="flex flex-wrap gap-2">
              <select value={serieSort} onChange={e => { setSerieSort(e.target.value); setSeriePage(1); }}
                className="bg-white/5 text-gray-300 text-sm rounded-lg px-3 py-1.5 outline-none border border-white/10 hover:border-white/20">
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <GridSection items={series} onItemClick={setSelectedItem} loading={loading.series} />
            <div className="flex items-center justify-center gap-3 pt-2">
              <button disabled={seriePage <= 1 || loading.series} onClick={() => setSeriePage(p => p - 1)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 transition-colors"><ChevronLeft size={18} /></button>
              <span className="text-gray-400 text-sm">{seriePage} / {Math.min(seriePages, 500)}</span>
              <button disabled={seriePage >= seriePages || loading.series} onClick={() => setSeriePage(p => p + 1)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 transition-colors"><ChevronRight size={18} /></button>
            </div>
          </>
        )}

        {activeTab === 'search' && (
          <>
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Buscar películas, series, actores..."
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-red-500/50 text-sm" autoFocus />
              {loading.search && <Loader size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 animate-spin" />}
            </div>
            {searchQuery.length > 0 && searchResults.length === 0 && !loading.search && (
              <div className="text-center py-12">
                <Search size={40} className="text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500">No se encontraron resultados para "{searchQuery}"</p>
              </div>
            )}
            {!searchQuery && (
              <div className="text-center py-16">
                <Film size={48} className="text-gray-700 mx-auto mb-4" />
                <p className="text-gray-400 text-lg font-medium">Busca cualquier película, serie o actor</p>
                <p className="text-gray-600 text-sm mt-1">Base de datos de más de 1 millón de títulos</p>
              </div>
            )}
            <GridSection items={searchResults} onItemClick={setSelectedItem} loading={loading.search} />
          </>
        )}
      </div>

      <div className="flex-shrink-0 px-4 py-2 border-t border-white/5 flex items-center justify-center gap-1.5">
        <img src="https://www.themoviedb.org/assets/2/v4/logos/v2/blue_short-8e7b30f73a4020692ccca9c88bafe5dcb6f8a62a4c6bc55cd9ba82bb2cd95f6c.svg" alt="TMDB" className="h-4 opacity-60" />
        <span className="text-gray-600 text-xs">Este producto usa la API de TMDB</span>
      </div>
    </div>
  
      </>
    );
}
